import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useInView } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import KineticHero from '../components/KineticHero';
import EditorialMarquee from '../components/EditorialMarquee';
import ServicesBentoGrid from '../components/ServicesBentoGrid';
import ClientLogoWall from '../components/ClientLogoWall';
import UpcomingEventsSection from '../components/UpcomingEventsSection';
import { Reveal, FadeIn } from '../components/Reveal';
import { IMAGES } from '../lib/site';

const METRICS = [
    { value: 2.4, decimals: 1, suffix: 'B+', label: 'Cumulative media impressions' },
    { value: 140, decimals: 0, suffix: '+', label: 'Global event activations' },
    { value: 85, decimals: 0, suffix: '+', label: 'C-suite advisory programs' },
    { value: 32, decimals: 0, suffix: '', label: 'Markets entered' },
];

const Counter = ({ value, decimals, suffix }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: '-60px' });
    const [display, setDisplay] = useState('0');

    useEffect(() => {
        if (!inView) return;
        const start = performance.now();
        const dur = 1800;
        let raf;
        const tick = (t) => {
            const p = Math.min((t - start) / dur, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            setDisplay((value * eased).toFixed(decimals));
            if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [inView, value, decimals]);

    return (
        <span ref={ref}>
            {display}
            <span className="text-bronze">{suffix}</span>
        </span>
    );
};

export default function Home() {
    return (
        <>
            <KineticHero />
            <EditorialMarquee />

            <section className="py-24 sm:py-32">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
                    <div className="lg:col-span-6">
                        <Reveal>
                            <p className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] text-bronze flex items-center gap-3">
                                <span className="inline-block w-10 h-px bg-bronze" />
                                The Firm
                            </p>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-cream leading-tight">
                                A decade of turning <span className="text-bronze">influence</span> into
                                market position.
                            </h2>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <p className="mt-7 text-base sm:text-lg text-fog leading-relaxed">
                                Since 2015, CommPro Worldwide has operated at the intersection of media,
                                capital, and policy — building scalable campaigns that combine media reach,
                                strategic partnerships, and industry engagement. We work directly with the
                                C-suite to define target market adoption and corporate development goals,
                                then design the programs that achieve them through our global network.
                            </p>
                        </Reveal>
                        <Reveal delay={0.3}>
                            <Link
                                to="/about"
                                data-testid="home-about-link"
                                className="mt-9 inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.22em] text-bronze group"
                            >
                                Our Methodology
                                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-300" />
                            </Link>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-6">
                        <FadeIn delay={0.2}>
                            <div className="relative">
                                <div className="absolute -inset-3 border border-bronze/25 -translate-x-5 translate-y-5 pointer-events-none" />
                                <img
                                    src={IMAGES.boardroom}
                                    alt="Executive boardroom strategy session"
                                    className="relative w-full aspect-[16/11] object-cover grayscale-[30%] contrast-110"
                                />
                            </div>
                        </FadeIn>
                    </div>
                </div>
            </section>

            <ServicesBentoGrid />
            <ClientLogoWall compact />
            <UpcomingEventsSection />

            <section className="dark-section py-24 sm:py-28 border-t border-line">
                <div className="max-w-7xl mx-auto px-5 sm:px-8">
                    <Reveal>
                        <p className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] text-bronze flex items-center gap-3">
                            <span className="inline-block w-10 h-px bg-bronze" />
                            Reach & Impact
                        </p>
                    </Reveal>
                    <div data-testid="impact-metrics" className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-y-12 gap-x-6">
                        {METRICS.map((m, i) => (
                            <Reveal key={m.label} delay={i * 0.1}>
                                <p className="font-serif text-4xl sm:text-5xl lg:text-6xl text-cream tracking-tight">
                                    <Counter value={m.value} decimals={m.decimals} suffix={m.suffix} />
                                </p>
                                <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-fog">
                                    {m.label}
                                </p>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section
                className="relative py-28 sm:py-36 overflow-hidden"
                style={{
                    '--ink': '21 18 46',
                    '--cream': '242 241 250',
                    '--fog': '155 157 192',
                    '--bronze': '154 160 245',
                }}
            >
                <img
                    src={IMAGES.skyscraper}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover opacity-20 grayscale-[40%]"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/85 to-ink/60" />
                <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
                    <Reveal>
                        <h2 className="font-serif text-3xl sm:text-4xl lg:text-6xl font-medium tracking-tight text-cream max-w-3xl leading-tight">
                            Your market is waiting. <span className="text-bronze">Let's enter it deliberately.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <Link
                            to="/contact"
                            data-testid="home-cta-btn"
                            className="group mt-10 inline-flex items-center gap-3 bg-bronze text-ink font-mono text-[11px] uppercase tracking-[0.22em] px-10 py-4 rounded-full relative overflow-hidden"
                        >
                            <span className="absolute inset-0 bg-gold translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out" />
                            <span className="relative">Begin the Conversation</span>
                            <ArrowRight size={14} className="relative group-hover:translate-x-1 transition-transform duration-300" />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </>
    );
}
